/* eslint-disable react-refresh/only-export-components */
import {
  DimeTooltip,
  DimeTooltipProvider,
  DimeTooltipRoot,
  DimeTooltipTrigger,
  DimeTooltipContent,
  DimeButton,
  DimeInfoHint,
  DimeSeparator,
} from '@nudool/design-system';
import { createFileRoute } from '@tanstack/react-router';
import * as React from 'react';

export const Route = createFileRoute('/design-system/tooltip')({
  component: TooltipDocs,
});

function DocsSection({ title, children }: Readonly<{ title: string; children: React.ReactNode }>) {
  return (
    <section className="mb-10">
      <h2 className="mb-1 text-xl font-semibold">{title}</h2>
      <DimeSeparator className="mb-4" />
      {children}
    </section>
  );
}

function Preview({ children }: Readonly<{ children: React.ReactNode }>) {
  return (
    <div className="flex flex-wrap items-center gap-6 rounded-lg border border-border bg-background p-10">
      {children}
    </div>
  );
}

function TooltipDocs() {
  return (
    <div>
      <h1 className="mb-1 text-3xl font-bold">Tooltip</h1>
      <p className="mb-8 text-muted-foreground">
        Contextual label that appears on hover. Use{' '}
        <code className="font-mono text-sm">DimeTooltip</code> for the simple one-liner API, or
        compose with the primitive parts for custom layouts.
      </p>

      <DocsSection title="Basic usage">
        <Preview>
          <DimeTooltip content="Save the current record">
            <DimeButton variant="outline">Hover me</DimeButton>
          </DimeTooltip>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Pass <code className="font-mono">content</code> and wrap any element as the child.{' '}
          <code className="font-mono">asChild</code> is <code className="font-mono">true</code> by
          default so the child element is used as the trigger directly.
        </p>
      </DocsSection>

      <DocsSection title="Rich JSX content">
        <Preview>
          <DimeTooltip
            content={
              <span className="flex flex-col gap-0.5">
                <span className="font-semibold">Keyboard shortcut</span>
                <span className="opacity-70">⌘ + S to save</span>
              </span>
            }
          >
            <DimeButton variant="outline">Rich tooltip</DimeButton>
          </DimeTooltip>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          The <code className="font-mono">content</code> prop accepts any{' '}
          <code className="font-mono">React.ReactNode</code>.
        </p>
      </DocsSection>

      <DocsSection title="Sides">
        <Preview>
          {(['top', 'right', 'bottom', 'left'] as const).map((side) => (
            <DimeTooltip key={side} content={`Side: ${side}`} side={side}>
              <DimeButton variant="secondary" size="sm">
                {side}
              </DimeButton>
            </DimeTooltip>
          ))}
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Control placement with the <code className="font-mono">side</code> prop:{' '}
          <code className="font-mono">"top" | "right" | "bottom" | "left"</code>. Default is{' '}
          <code className="font-mono">"top"</code>.
        </p>
      </DocsSection>

      <DocsSection title="Alignments">
        <Preview>
          {(['start', 'center', 'end'] as const).map((align) => (
            <DimeTooltip key={align} content={`Align: ${align}`} side="bottom" align={align}>
              <DimeButton variant="secondary" size="sm">
                {align}
              </DimeButton>
            </DimeTooltip>
          ))}
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">align</code> to shift the tooltip along its axis:{' '}
          <code className="font-mono">"start" | "center" | "end"</code>. Default is{' '}
          <code className="font-mono">"center"</code>.
        </p>
      </DocsSection>

      <DocsSection title="Info hint">
        <Preview>
          <DimeInfoHint content="Reps fill in the product columns for each product on the event." />
          <DimeInfoHint
            content="Tax and discount are recalculated when you review."
            size="icon-xs"
          />
          <DimeInfoHint
            label="How the total is worked out"
            variant="card"
            content={<p>Tax and discount are added to the line subtotal.</p>}
          />
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          <code className="font-mono">DimeInfoHint</code> is a focusable info icon. Its text is also
          its accessible name, and a tap opens it on touch screens. It backs the{' '}
          <code className="font-mono">hint</code> on{' '}
          <code className="font-mono">DimeFieldLabel</code>. Pass{' '}
          <code className="font-mono">size=&quot;icon-xs&quot;</code> for a 24px tap target where a
          phone user has to tap it. Content can be a node when{' '}
          <code className="font-mono">label</code> names the button.
        </p>
      </DocsSection>

      <DocsSection title="Composing with primitives">
        <Preview>
          <DimeTooltipProvider>
            <DimeTooltipRoot>
              <DimeTooltipTrigger asChild>
                <DimeButton variant="ghost" size="sm">
                  Custom trigger
                </DimeButton>
              </DimeTooltipTrigger>
              <DimeTooltipContent side="right">
                Built from primitives — full control
              </DimeTooltipContent>
            </DimeTooltipRoot>
          </DimeTooltipProvider>
        </Preview>
        <p className="mt-3 text-sm text-muted-foreground">
          Use <code className="font-mono">DimeTooltipProvider</code>,{' '}
          <code className="font-mono">DimeTooltipRoot</code>,{' '}
          <code className="font-mono">DimeTooltipTrigger</code>, and{' '}
          <code className="font-mono">DimeTooltipContent</code> directly when you need granular
          control (e.g., sharing a single Provider across many tooltips).
        </p>
      </DocsSection>
    </div>
  );
}
